'use client';

import { useCallback, useMemo, useRef } from 'react';

import { FlaskConical } from 'lucide-react';

import {
  type CombinationDrug,
  DrugInteractionsPageShell,
  type InitialPanel,
  type InteractionCheckResult,
  type PaywallVariant,
} from '@kit/drug-interactions';
import type { Drug } from '@kit/drug-interactions/components/drug-search';
import {
  BAZA_LEKOW_PREFIX,
  type PanelType,
} from '@kit/drug-interactions/lib/slug';
import { useUser } from '@kit/supabase/hooks/use-user';

import { DrugInteractionsPageContent } from '~/(home)/_components/drug-interactions-page-content';
import { HomeBottomSections } from '~/(home)/_components/home-bottom-sections';
import { SitePageHeader } from '~/(marketing)/_components/site-page-header';
import { AppHeaderContent, type ProfileInfo } from '~/components/app-header';
import { SyncBarFixed } from '~/components/sync-bar-fixed';
import { getMaxDrugsForTier } from '~/config/subscription-tiers.config';
import { useDatabaseStats } from '~/lib/contexts/database-stats-context';
import { useSectionVisibility } from '~/lib/contexts/section-visibility-context';
import { useResetOnLogoClick } from '~/lib/hooks/use-reset-on-logo-click';
import type { IncrediblesBarVariant } from '~/lib/incredibles-bar';

interface BazaLekowPageContentProps {
  isAuthenticated: boolean;
  initialDrugs?: Drug[];
  extractionStats?: {
    extractedCount: number;
    totalCount: number;
    cenCount: number;
  };
  userEmail?: string;
  profile?: ProfileInfo | null;
  hasActiveSubscription?: boolean;
  isSuperAdmin?: boolean;
  maxDrugs?: number;
  hasCustomPlan?: boolean;
  panelType?: PanelType;
  panelDrugId?: string;
  initialInteractionResults?: InteractionCheckResult[];
  initialInteractionAccess?: 'none' | 'full' | 'paywalled';
  initialInteractionPaywallVariant?: PaywallVariant | null;
  usePrimaryBrandHeading?: boolean;
  initialTopCombinations?: CombinationDrug[];
  /** InCredibles bar variant for anonymous visitors (null = hidden) */
  incrediblesBar?: IncrediblesBarVariant | null;
}

/**
 * Extract the bare drug slug from a single-drug /baza-lekow URL.
 * Returns null for multi-drug (/interakcje/) or unrecognised paths.
 */
function extractSingleDrugSlug(pathname: string): string | null {
  if (!pathname.startsWith(BAZA_LEKOW_PREFIX + '/')) return null;
  const rest = pathname.slice(BAZA_LEKOW_PREFIX.length + 1);
  if (!rest || rest.startsWith('interakcje/')) return null;
  if (rest.startsWith('chpl/')) return rest.slice('chpl/'.length);
  if (rest.startsWith('ulotka/')) return rest.slice('ulotka/'.length);
  return rest;
}

export function BazaLekowPageContent({
  isAuthenticated: isAuthenticatedServer,
  initialDrugs,
  extractionStats,
  userEmail,
  profile,
  hasActiveSubscription,
  isSuperAdmin,
  maxDrugs,
  hasCustomPlan = false,
  panelType,
  panelDrugId,
  initialInteractionResults,
  initialInteractionAccess = 'none',
  initialInteractionPaywallVariant = null,
  usePrimaryBrandHeading: _usePrimaryBrandHeading = true,
  initialTopCombinations,
  incrediblesBar = null,
}: BazaLekowPageContentProps) {
  const { data: user, isLoading: isUserLoading } = useUser();
  const isAuthenticated = isUserLoading ? isAuthenticatedServer : !!user;
  // Fall back to the anonymous/trial default (Premium tier) when no tier-derived cap
  // was passed in — prevents the 20-drug MAX_DRUGS_FALLBACK from leaking
  // through for unauthenticated visitors on /baza-lekow.
  const effectiveMaxDrugs = maxDrugs ?? getMaxDrugsForTier(null);
  const dbStats = useDatabaseStats();
  const { main_pricing, nav_pricing } = useSectionVisibility();
  // `nav_*` governs logged-in users, `main_*` anonymous visitors (also while
  // the client auth state is still loading for a server-anonymous request)
  const showPricing = isAuthenticated ? nav_pricing : main_pricing;
  const resetRef = useRef<(() => void) | null>(null);
  const setDrugsRef = useRef<((drugs: Drug[]) => void) | null>(null);

  const handleLogoClick = useResetOnLogoClick(resetRef);

  const handleActivateTrial = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openTrialWizard'));
  }, []);

  const initialPanel: InitialPanel | undefined = useMemo(() => {
    if (!panelType || !panelDrugId || !initialDrugs) return undefined;
    const drug = initialDrugs.find((d) => d.id === panelDrugId);
    if (!drug) return undefined;
    return { type: panelType, drug };
  }, [panelType, panelDrugId, initialDrugs]);

  const handlePanelChange = useCallback(
    (panel: { type: 'chpl' | 'ulotka'; drugId: string } | null) => {
      const pathname = window.location.pathname;
      const isMultiDrug = pathname.startsWith(
        BAZA_LEKOW_PREFIX + '/interakcje/',
      );

      if (isMultiDrug) {
        const url = new URL(window.location.href);
        url.searchParams.delete('chpl');
        url.searchParams.delete('ulotka');
        if (panel) {
          url.searchParams.set(panel.type, panel.drugId);
        }
        window.history.replaceState({}, '', url.pathname + url.search);
      } else {
        const drugSlug = extractSingleDrugSlug(pathname);
        if (!drugSlug) return;

        if (panel) {
          window.history.replaceState(
            {},
            '',
            `${BAZA_LEKOW_PREFIX}/${panel.type}/${drugSlug}`,
          );
        } else {
          window.history.replaceState(
            {},
            '',
            `${BAZA_LEKOW_PREFIX}/${drugSlug}`,
          );
        }
      }
    },
    [],
  );

  // ─── Unauthenticated: delegate to the shared homepage component ────────────
  // Guard on both server and client auth state to avoid a layout flash during
  // hydration when the server says unauth but the client hasn't settled yet.
  if (!isAuthenticatedServer && !isAuthenticated && !isUserLoading) {
    const stats = extractionStats ?? dbStats;

    return (
      <DrugInteractionsPageContent
        initialDrugs={initialDrugs}
        initialPanel={initialPanel}
        initialInteractionResults={initialInteractionResults}
        initialInteractionAccess={initialInteractionAccess}
        initialInteractionPaywallVariant={initialInteractionPaywallVariant}
        initialTopCombinations={initialTopCombinations}
        showUnavailableDrugs
        onPanelChange={handlePanelChange}
        maxDrugs={effectiveMaxDrugs}
        incrediblesBar={incrediblesBar}
        aboveFooter={
          stats ? (
            <HomeBottomSections
              extractionStats={stats}
              lastSyncAt={dbStats?.lastSyncAt}
            />
          ) : undefined
        }
      />
    );
  }

  // ─── Authenticated (or loading) ────────────────────────────────────────────

  const header = !isUserLoading ? (
    <AppHeaderContent
      userEmail={userEmail ?? user?.email ?? user?.phone ?? ''}
      profile={profile}
      onActivateTrial={handleActivateTrial}
      onLogoClick={handleLogoClick}
      isSuperAdmin={isSuperAdmin}
    />
  ) : null;

  const needsTrialActivation =
    isAuthenticated && !hasActiveSubscription && profile?.hasProfile === false;

  const branding = <SitePageHeader title="Analiza interakcji" />;

  return (
    <DrugInteractionsPageShell
      showPricing={showPricing}
      isAuthenticated={isAuthenticated}
      isAuthLoading={isUserLoading}
      hasActiveTrial={hasActiveSubscription}
      needsTrialActivation={needsTrialActivation}
      onActivateTrial={handleActivateTrial}
      initialDrugs={initialDrugs}
      enableUrlSync
      header={header}
      branding={branding}
      searchInputClassName={({ hasDrugs }) =>
        hasDrugs ? 'rounded-xl' : 'sm:h-16 rounded-xl'
      }
      searchPlaceholder="Wyszukaj lek po nazwie, substancji czynnej..."
      emptyStatePaddingTop="pt-[12vh]"
      searchInputEndAddon={({ handleLoadExample }) => (
        <button
          type="button"
          onClick={() => void handleLoadExample()}
          className="cursor-pointer rounded-lg bg-stone-100 px-2.5 py-2 text-xs font-medium whitespace-nowrap text-green-600 transition-colors hover:bg-stone-200 hover:text-green-700 dark:bg-stone-800/50 dark:text-green-400 dark:hover:bg-stone-700/50 dark:hover:text-green-300"
        >
          <FlaskConical className="-mt-0.5 inline h-3.5 w-3.5" /> Zobacz
          przykładową analizę
        </button>
      )}
      searchFooterOverride={({ handleLoadExample }) => (
        <div className="mt-2 flex justify-center sm:hidden">
          <button
            type="button"
            onClick={() => void handleLoadExample()}
            className="cursor-pointer rounded-lg bg-stone-100 px-3.5 py-1.5 text-xs font-medium text-green-600 transition-colors hover:bg-stone-200 hover:text-green-700 dark:bg-stone-800/50 dark:text-green-400 dark:hover:bg-stone-700/50 dark:hover:text-green-300"
          >
            Zobacz przykładową analizę →
          </button>
        </div>
      )}
      footer={({ isViewerOpen, panelWidth }) => (
        <>
          <div className="mt-auto pb-14" />
          <SyncBarFixed isViewerOpen={isViewerOpen} panelWidth={panelWidth} />
        </>
      )}
      initialPanel={initialPanel}
      initialInteractionResults={initialInteractionResults}
      initialInteractionAccess={initialInteractionAccess}
      initialInteractionPaywallVariant={initialInteractionPaywallVariant}
      onPanelChange={handlePanelChange}
      showUnavailableDrugs
      initialTopCombinations={initialTopCombinations}
      maxDrugs={effectiveMaxDrugs}
      hasCustomPlan={hasCustomPlan}
      onResetRef={(reset) => {
        resetRef.current = reset;
      }}
      onSetDrugsRef={(setDrugs) => {
        setDrugsRef.current = setDrugs;
      }}
    />
  );
}
